/**
 * Client State Management
 *
 * Zustand store for client-side state (UI state, filters, preferences)
 * Server state (listings, messages) is managed by React Query
 */

import { create } from "zustand";
import { Account } from "./api";

// ============================================================================
// Auth Store
// ============================================================================

interface AuthState {
  account: Account | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  setAccount: (account: Account | null) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  account: null,
  isLoading: false,
  error: null,

  setAccount: (account) => set({ account, error: null }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  logout: () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    set({ account: null, error: null });
  },
}));

// ============================================================================
// Search/Filter Store
// ============================================================================

interface SearchState {
  filters: {
    search: string;
    type: string[];
    minPrice: number | null;
    maxPrice: number | null;
    location: string;
    minLength: number | null;
    maxLength: number | null;
  };

  // Actions
  setSearch: (search: string) => void;
  setType: (type: string[]) => void;
  setPriceRange: (min: number | null, max: number | null) => void;
  setLocation: (location: string) => void;
  setLengthRange: (min: number | null, max: number | null) => void;
  resetFilters: () => void;
}

const defaultFilters = {
  search: "",
  type: [] as string[],
  minPrice: null,
  maxPrice: null,
  location: "",
  minLength: null,
  maxLength: null,
};

export const useSearchStore = create<SearchState>((set) => ({
  filters: defaultFilters,

  setSearch: (search) => set((state) => ({ filters: { ...state.filters, search } })),
  setType: (type) => set((state) => ({ filters: { ...state.filters, type } })),
  setPriceRange: (minPrice, maxPrice) =>
    set((state) => ({ filters: { ...state.filters, minPrice, maxPrice } })),
  setLocation: (location) => set((state) => ({ filters: { ...state.filters, location } })),
  setLengthRange: (minLength, maxLength) =>
    set((state) => ({ filters: { ...state.filters, minLength, maxLength } })),
  resetFilters: () => set({ filters: defaultFilters }),
}));

// ============================================================================
// UI State Store
// ============================================================================

interface UiState {
  sidebarOpen: boolean;
  mobileMenuOpen: boolean;
  theme: "light" | "dark";
  notifications: Array<{
    id: string;
    type: "success" | "error" | "info" | "warning";
    message: string;
    duration?: number;
  }>;

  // Actions
  toggleSidebar: () => void;
  setMobileMenuOpen: (open: boolean) => void;
  setTheme: (theme: "light" | "dark") => void;
  addNotification: (
    message: string,
    type: "success" | "error" | "info" | "warning",
    duration?: number
  ) => void;
  removeNotification: (id: string) => void;
}

export const useUiStore = create<UiState>((set) => ({
  sidebarOpen: true,
  mobileMenuOpen: false,
  theme: "light",
  notifications: [],

  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  setMobileMenuOpen: (open) => set({ mobileMenuOpen: open }),
  setTheme: (theme) => set({ theme }),
  addNotification: (message, type, duration = 5000) =>
    set((state) => ({
      notifications: [
        ...state.notifications,
        {
          id: `${Date.now()}-${Math.random()}`,
          type,
          message,
          duration,
        },
      ],
    })),
  removeNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),
}));

// ============================================================================
// Draft Listings Store
// ============================================================================

interface DraftListing {
  id?: string;
  title: string;
  description: string;
  price: number;
  currency: "USD" | "EUR" | "GBP" | "AUD";
  location: string;
  type: "powerboat" | "sailboat" | "yacht" | "catamaran" | "other";
  year: number;
  length: number;
  lengthUnit: "feet" | "meters";
  make: string;
  model: string;
  mediaUrls: string[];
  packageTier?: "base" | "featured" | "top";
}

interface DraftStore {
  draft: DraftListing | null;
  setDraft: (draft: DraftListing) => void;
  updateDraft: (partial: Partial<DraftListing>) => void;
  clearDraft: () => void;
}

const defaultDraft: DraftListing = {
  title: "",
  description: "",
  price: 0,
  currency: "USD",
  location: "",
  type: "powerboat",
  year: new Date().getFullYear(),
  length: 0,
  lengthUnit: "feet",
  make: "",
  model: "",
  mediaUrls: [],
};

export const useDraftStore = create<DraftStore>((set) => ({
  draft: null,

  setDraft: (draft) => set({ draft }),
  updateDraft: (partial) =>
    set((state) => ({
      draft: state.draft ? { ...state.draft, ...partial } : { ...defaultDraft, ...partial },
    })),
  clearDraft: () => set({ draft: null }),
}));
